/**
 * Map the phase names emitted by our state machine → the keys the soundpack
 * admin panel uses. The soundpacks pilots upload cover these 8 phases:
 *   BOARDING · PUSHBACK · INFLIGHT · TURBULENCE
 *   DESCENT  · APPROACH · ARRIVAL  · DEPLANING
 * Phases without a good mapping just don't fire a clip.
 */
export const PHASE_TO_SOUND_KEY = {
  "BOARDING": "BOARDING",
  "TAXI OUT": "PUSHBACK",
  "CRUISE":   "INFLIGHT",
  "DESCENT":  "DESCENT",
  "APPROACH": "APPROACH",
  "TAXI IN":  "ARRIVAL",
  "ARRIVED":  "DEPLANING",
};

/**
 * The order a flight meets its announcements in. A clip plays only when the
 * flight has moved past every clip already reached, so each one is heard at
 * most once per flight and never out of order: a phase that wobbles back
 * (a level-off mid-descent read as cruise, reported 2026-10-04; a go-around
 * back into APPROACH) cannot replay the cruise service or the descent PA.
 * TURBULENCE is played by hand only, so it is not in the order.
 */
export const SOUND_ORDER = ["BOARDING", "PUSHBACK", "INFLIGHT", "DESCENT", "APPROACH", "ARRIVAL", "DEPLANING"];

/** The sound key a new phase should play, and the flight's new furthest point;
 *  key is null when nothing should play. `reached` is -1 at the start of a flight. */
export function nextSound(phase, reached) {
  const key = PHASE_TO_SOUND_KEY[phase] || phase;
  const at = SOUND_ORDER.indexOf(key);
  return at > reached ? { key, reached: at } : { key: null, reached };
}

// The arrival PA waits for taxi speed. TAXI IN starts at touchdown, so it used
// to play on the landing roll (beta report 2026-10-07).
export const ARRIVAL_BELOW_KT = 25;

// The in-flight announcement plays this long after wheels-off, in the climb,
// as cabin crew make it a minute or so after the gear comes up. It used to
// wait for CRUISE, which below the planned level means ten minutes level
// (beta report 2026-10-16). Reaching cruise sooner still plays it then.
export const INFLIGHT_AFTER_OFF_S = 90;

// The approach announcement (cabin secure) plays descending through 10,000 ft,
// where crews make it. It used to wait for the APPROACH phase at 5,000 ft
// (beta report 2026-10-16). Only the clip moves: the phase is unchanged. A
// flight that cruises below 10,000 ft hears it at the start of the descent,
// and the descent PA is then skipped, as there is no time for both.
export const APPROACH_PA_BELOW_FT = 10000;

/** The phase the announcements follow: TAXI IN only once slowed to taxi
 *  speed; the climb counts as INFLIGHT from INFLIGHT_AFTER_OFF_S after OFF;
 *  the descent counts as APPROACH below APPROACH_PA_BELOW_FT. `active.spd` is
 *  ground speed in knots, `active.alt` the altimeter in feet, `active.time_off`
 *  epoch seconds. */
export function soundPhase(phase, active, nowSec = Date.now() / 1000) {
  if (phase === "TAXI IN" && (Number(active?.spd) || 0) >= ARRIVAL_BELOW_KT) return null;
  const off = Number(active?.time_off) || 0;
  if (phase === "CLIMB" && off > 0 && nowSec - off >= INFLIGHT_AFTER_OFF_S) return "INFLIGHT";
  if (phase === "DESCENT" && active?.alt != null && Number(active.alt) < APPROACH_PA_BELOW_FT) return "APPROACH";
  return phase;
}
